import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookingsType } from '../types/types';
import { styled } from 'styled-components';
import { Col, DatePicker, Row, Select } from 'antd';
import { bookingStatus, COLORS, TEXT_SIZES } from '../utils/constants';
import {
  LoadingOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { debounce } from '../utils/utils';
import { cancelBooking, getAllBookings } from '../api/requests/bookings';
import { CustomTable } from '../components/common/CustomTable';
import Modal from '../components/common/Modal';
import { searchFacilitiesRequest } from '../api/requests/facility';
import Button from '../components/common/Button';
import { Dayjs } from 'dayjs';
import { getSportsRequest } from '../api/requests/sports';
import { useSnackbar } from '../contexts/SnackbarContext/SnackbarProvider';
import { StyledSearch } from './UserManagement';
import ViewBookingDrawer from '../components/features/FacilityBookings/ViewBookingDrawer';

const { RangePicker } = DatePicker;

const Bookings = () => {
  const snackbar = useSnackbar();

  // --- State ---
  const [bookingsList, setBookingsList] = useState<BookingsType[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<
    number | undefined
  >();
  const [selectedBookingStatus, setSelectedBookingStatus] = useState<
    number | undefined
  >();
  const [selectedSport, setSelectedSport] = useState<number | undefined>();
  const [cancelBookingSlot, setCancelBookingSlot] = useState<{
    bookingId: number;
    updatedBy: number;
  } | null>(null);
  const [sportsList, setSportsList] =
    useState<{ label: string; value: number }[]>();
  const [facilities, setFacilities] =
    useState<{ label: string; value: number }[]>();
  const [dateFilter, setDateFilter] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [searchBookings, setSearchBookings] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null,
  );
  const [isBookingCancelling, setIsBookingCancelling] =
    useState<boolean>(false);

  const pageSize = 8;

  const filters = [
    {
      options: facilities,
      setter: setSelectedFacility,
      placeholder: 'Select Facility',
    },
    {
      options: bookingStatus,
      setter: setSelectedBookingStatus,
      placeholder: 'Select Status',
    },
    {
      options: sportsList,
      setter: setSelectedSport,
      placeholder: 'Select Sport',
    },
  ];

  const getBookings = useCallback(
    async (params?: { query?: string; page?: number }) => {
      try {
        setLoading(true);
        const data = await getAllBookings({
          sportId: selectedSport,
          facilityId: selectedFacility,
          status: selectedBookingStatus,
          SearchQuery: params?.query?.trim() || undefined,
          fromDate: dateFilter?.[0]
            ? String(dateFilter[0].startOf('day'))
            : undefined,
          toDate: dateFilter?.[1]
            ? String(dateFilter[1].endOf('day'))
            : undefined,
          PageNumber: params?.page ? params.page - 1 : currentPage - 1,
          PageSize: pageSize,
        });
        setBookingsList(data?.value?.rows || []);
        setTotalCount(data?.value?.totalCount || 0);
        setCurrentPage(params?.page || currentPage);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedSport,
      selectedBookingStatus,
      selectedFacility,
      dateFilter,
      currentPage,
    ],
  );

  const handleFilterChange = (
    setter: React.Dispatch<React.SetStateAction<number | undefined>>,
    value: number | undefined,
  ) => {
    setter(value);
    setCurrentPage(1);
  };

  const dateChangeHandler = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateFilter(dates);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => {
    getBookings({ query: searchBookings, page });
  };

  const cancelBookingHandler = async () => {
    try {
      if (cancelBookingSlot) {
        setIsBookingCancelling(true);
        const response = await cancelBooking(cancelBookingSlot);
        if (response?.isSuccess) {
          setCancelBookingSlot(null);
          getBookings({ query: searchBookings, page: currentPage });
          snackbar.success('Booking cancelled successfully');
        } else {
          snackbar.error(response?.error);
        }
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error cancelling booking');
    } finally {
      setIsBookingCancelling(false);
    }
  };

  const getSports = async () => {
    try {
      const data = await getSportsRequest();
      if (data?.value) {
        setSportsList(
          data.value.map((item) => ({
            label: item?.sportName,
            value: item?.sportId,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching sports list:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const data = await searchFacilitiesRequest();
      if (data?.value?.rows) {
        setFacilities(
          data.value.rows.map((item) => ({
            label: item?.facilityName,
            value: item?.facilityId,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleOpenDrawer = (bookingId: number) => {
    setOpenDrawer(true);
    setSelectedBookingId(bookingId);
  };
  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedBookingId(null);
  };
  const handleSlotCancelled = useCallback(() => {
    getBookings({ query: searchBookings, page: currentPage });
  }, [getBookings, searchBookings, currentPage]);

  const columns = [
    {
      title: 'Booking ID',
      dataIndex: 'bookingReference',
      key: 'booking-Id',
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'user-Name',
      render: (text: string, row: any) => (
        <div>
          <span>{text}</span>
          <PhoneNumberAndEmailWrapper>
            <IconWrapper>
              <PhoneIconWrapper rotate={90} />
            </IconWrapper>
            <GrayColoredText>{row?.phoneNumber}</GrayColoredText>
          </PhoneNumberAndEmailWrapper>
          <PhoneNumberAndEmailWrapper>
            <IconWrapper>
              <MailIconWrapper />
            </IconWrapper>
            <GrayColoredText>{row?.email}</GrayColoredText>
          </PhoneNumberAndEmailWrapper>
        </div>
      ),
    },
    {
      title: 'Facility & Sport',
      dataIndex: 'facilityName',
      key: 'facility-Name',
      render: (text: string, row: any) => (
        <VerticalAlign>
          <span>{text}</span>
          <GrayColoredText>{row?.sportName}</GrayColoredText>
        </VerticalAlign>
      ),
    },
    {
      title: 'Date & Time',
      dataIndex: 'bookingDate',
      key: 'booking-Date',
      render: (text: Date) => {
        const value = moment(text);
        return (
          <VerticalAlign>
            <span>{value.format('DD/MM/YYYY')}</span>
            <GrayColoredText>{value.format('hh:mm A')}</GrayColoredText>
          </VerticalAlign>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'statusName',
      key: 'status-name',
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'total-Amount',
      render: (text: string) => <span>Rs {text}</span>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (_: string, row: BookingsType) => (
        <ActionWrapper>
          <ActionText onClick={() => handleOpenDrawer(row.bookingId)}>
            View
          </ActionText>
          <EditAndButtonText
            onClick={() => {
              if (
                row?.statusName !== 'Cancelled' &&
                row?.statusName !== 'Completed'
              ) {
                setCancelBookingSlot({
                  bookingId: row?.bookingId,
                  updatedBy: row?.userId,
                });
              }
            }}
            status={
              row?.statusName === 'Cancelled' || row?.statusName === 'Completed'
            }
          >
            Cancel
          </EditAndButtonText>
        </ActionWrapper>
      ),
    },
  ];

  useEffect(() => {
    getSports();
    fetchFacilities();
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        getBookings({
          query: value,
          page: 1,
        });
      }, 500),
    [selectedFacility, selectedBookingStatus, selectedSport, dateFilter],
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchBookings(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    getBookings({ query: searchBookings, page: 1 });
  }, [selectedSport, selectedFacility, selectedBookingStatus, dateFilter]);

  return (
    <Container gutter={[24, 24]}>
      <Col span={24}>
        <StyledSearch
          placeholder="Search by booking ID, username..."
          onChange={handleSearch}
          value={searchBookings}
          allowClear
          size="large"
          enterButton
        />
      </Col>
      <Col span={20}>
        <Row gutter={16}>
          {filters.map((filter, idx) => (
            <Col span={6} key={idx}>
              <Select
                options={filter.options}
                onChange={(value) =>
                  handleFilterChange(filter.setter, value as number | undefined)
                }
                placeholder={filter.placeholder}
                style={{ width: '90%', height: '43px' }}
                allowClear
              />
            </Col>
          ))}
          <Col span={6}>
            <StyledRangePicker onChange={dateChangeHandler} />
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <CustomTable
          rowKey="bookingId"
          dataSource={bookingsList}
          columns={columns}
          pagination={{
            pageSize,
            current: currentPage,
            total: totalCount,
            onChange: handlePageChange,
          }}
          loading={loading}
        />
      </Col>

      <Modal
        open={!!cancelBookingSlot}
        title="Cancel Booking"
        onCancel={() => setCancelBookingSlot(null)}
        headerBgColor={COLORS.red.primary}
        footerAlign="end"
        minHeight="auto"
        footer={
          <>
            <Button
              key="cancel"
              disabled={isBookingCancelling}
              onClick={() => setCancelBookingSlot(null)}
            >
              No
            </Button>
            <Button
              key="submit"
              type="primary"
              danger
              disabled={isBookingCancelling}
              onClick={cancelBookingHandler}
            >
              Yes
              {isBookingCancelling && <StyledLoadingOutlined spin />}
            </Button>
          </>
        }
      >
        <span>Are you sure you want to cancel this booking?</span>
      </Modal>
      {openDrawer && selectedBookingId && (
        <ViewBookingDrawer
          open={openDrawer}
          onClose={handleDrawerClose}
          bookingId={selectedBookingId}
          cancelSlot={true}
          onSlotCancelled={handleSlotCancelled}
        />
      )}
    </Container>
  );
};

// --- Styled Components ---
const Container = styled(Row)`
  padding: 8px;
`;

const PhoneNumberAndEmailWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconWrapper = styled.div`
  background-color: ${COLORS.grey.secondaryLight};
  border-radius: 10px;
  height: 18px;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PhoneIconWrapper = styled(PhoneOutlined)`
  height: 10px;
  width: 10px;
`;

const MailIconWrapper = styled(MailOutlined)`
  height: 10px;
  width: 10px;
`;

const GrayColoredText = styled.span`
  color: ${COLORS.grey.secondary};
  font-size: ${TEXT_SIZES.XS};
`;

const VerticalAlign = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditAndButtonText = styled.div<{ status: boolean }>`
  color: ${(props) =>
    props.status ? COLORS.grey.secondary : COLORS.skyBlue.primary};
  cursor: ${(props) => (props.status ? 'not-allowed' : 'pointer')};
`;

const StyledRangePicker = styled(RangePicker)`
  height: 43px;
`;

export default Bookings;

const ActionWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionText = styled.span`
  color: ${COLORS.blue.medium};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: ${COLORS.blue.medium};
    text-decoration: underline;
  }
`;

const StyledLoadingOutlined = styled(LoadingOutlined)`
  margin-left: 4px;
`;
